import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff, HelpCircle, Leaf, RotateCcw, Share2, Sparkles, Timer } from "lucide-react";
import { LIMITS, type ProgressRecord } from "../../shared/model";
import { Link, navigate } from "../app/router";
import * as store from "../app/store";
import { useStore } from "../app/store";
import { useThinkTimer } from "../app/timer";
import { clock, dayKey } from "../app/clock";
import { Button, LinkButton, Modal, useToast } from "../ui/kit";
import { Shell } from "../ui/Shell";
import { BOARDS } from "../puzzles/registry";
import { FAMILY_META, describe, furtherFor, hintsFor } from "../puzzles/catalog";
import { parsePuzzleId } from "../../shared/model";
import { chime } from "../app/sound";

/** what we keep in a record's `state`: the board's own state plus how long into the puzzle the last nudge came */
interface PageState {
  b: unknown;
  h: number;
}

/** think-seconds that must pass before the next nudge (or the answer) is offered */
const NUDGE_GAP = [40, 50, 50, 75];

function readState(rec: ProgressRecord | undefined): PageState {
  const s = rec?.state as Partial<PageState> | null | undefined;
  return { b: s && "b" in s ? s.b : null, h: typeof s?.h === "number" ? s.h : 0 };
}

function NotFound() {
  return (
    <Shell>
      <div className="page-center">
        <div className="lost-card">
          <img src="/art/lost.webp" alt="Pebble the turtle, lost on a lily pad" width="420" height="280" />
          <h1>That puzzle wandered off.</h1>
          <p className="muted">Let's find you another one.</p>
          <LinkButton to="/today" kind="primary">Back to today</LinkButton>
        </div>
      </div>
    </Shell>
  );
}

function nextPuzzle(currentId: string): string | null {
  const d = store.get();
  const today = dayKey();
  const plan = d.assign[today] ?? {};
  for (const fam of ["cut", "stars", "coin"] as const) {
    const pid = plan[fam];
    if (pid && pid !== currentId && d.progress[pid]?.status !== "solved" && d.progress[pid]?.status !== "revealed") return pid;
  }
  return null;
}

export default function PuzzlePage({ id }: { id: string }) {
  const parsed = parsePuzzleId(id);
  const info = useMemo(() => describe(id), [id]);
  if (!parsed || !info) return <NotFound />;
  return <PuzzleView key={id} id={id} family={parsed.family} />;
}

function PuzzleView({ id, family }: { id: string; family: "cut" | "stars" | "coin" }) {
  const toast = useToast();
  const info = describe(id)!;
  const meta = FAMILY_META[family];
  const Board = BOARDS[family];
  const rec = useStore((d) => d.progress[id]);
  const goalMinutes = useStore((d) => d.settings.goalMinutes);
  const solved = rec?.status === "solved";
  const revealed = rec?.status === "revealed";
  const hintLevel = Math.min(3, rec?.hints ?? 0);
  const hints = useMemo(() => hintsFor(id), [id]);
  const further = useMemo(() => furtherFor(id), [id]);
  const [restartToken, setRestartToken] = useState(0);
  const [answerOn, setAnswerOn] = useState(revealed);
  const [confirmReveal, setConfirmReveal] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [ahaOpen, setAhaOpen] = useState(false);
  // after solving, poking at the board again (another way, another try) still counts toward today, but not toward the solve time
  const [exploring, setExploring] = useState(false);
  const { seconds, paused } = useThinkTimer(family, id, !solved || exploring, !solved);
  useEffect(() => {
    document.title = `${info.title} · ${meta.name} · Ponder`;
  }, [info.title, meta.name]);
  const pageState = readState(rec);

  // a puzzle that was opened counts as "started", so it appears in the shelf as in progress
  const saved = useMemo(() => pageState.b, []); // eslint-disable-line react-hooks/exhaustive-deps
  const recRef = useRef(rec);
  recRef.current = rec;
  const secondsRef = useRef(seconds);
  secondsRef.current = seconds;

  const onState = useCallback((b: unknown) => {
    const cur = recRef.current;
    const text = JSON.stringify(b);
    if (text.length > LIMITS.stateBytes) return;
    if (cur?.status === "solved") setExploring(true);
    store.patchProgress(id, family, { state: { b, h: readState(cur).h } });
  }, [id, family]);

  const onSolved = useCallback((result: string, peeked?: boolean) => {
    const cur = recRef.current;
    if (cur?.status === "solved") {
      setExploring(false); // solved again (or another way): needs no fanfare from the page
      return;
    }
    const helped = peeked || cur?.status === "revealed";
    store.patchProgress(id, family, {
      status: "solved", solvedAt: Date.now(), result: (helped ? `${result} · with a peek` : result).slice(0, LIMITS.resultChars)
    });
    store.countSolvedToday();
    setAnswerOn(false);
    chime(store.get().settings.sound);
    // let the solved board shine for a moment before the modal appears
    setTimeout(() => setAhaOpen(true), 1000);
  }, [id, family]);

  // announce reaching the daily goal once
  const goalToast = useRef(false);
  const todaySecs = useStore((d) => {
    const t = d.days[dayKey()];
    return t ? t.cut + t.stars + t.coin : 0;
  });
  useEffect(() => {
    if (!goalToast.current && todaySecs >= goalMinutes * 60 && todaySecs > 0 && !solved) {
      const key = "ponder.goal." + dayKey();
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        goalToast.current = true;
        toast(`${goalMinutes} minutes of thinking today. That's your daily workout done!`);
        chime(store.get().settings.sound);
      }
    }
  }, [todaySecs, goalMinutes, solved, toast]);

  // nudges
  const gap = NUDGE_GAP[Math.min(hintLevel, 3)];
  const sinceHint = Math.max(0, seconds - pageState.h);
  const wait = Math.max(0, gap - sinceHint);
  const takeNudge = () => {
    if (hintLevel >= 3 || wait > 0) return;
    store.patchProgress(id, family, { hints: hintLevel + 1, state: { b: readState(recRef.current).b, h: secondsRef.current } });
  };

  const revealAnswer = () => {
    setConfirmReveal(false);
    if (!solved) store.patchProgress(id, family, { status: "revealed", state: { b: readState(recRef.current).b, h: secondsRef.current } });
    setAnswerOn(true);
  };

  const restart = () => {
    setConfirmRestart(false);
    setAnswerOn(false);
    setRestartToken((n) => n + 1);
  };

  const [noteDraft, setNoteDraft] = useState(rec?.note ?? "");
  const saveNote = () => {
    const cur = recRef.current;
    if (noteDraft.trim() === (cur?.note ?? "").trim()) return;
    store.patchProgress(id, family, { note: noteDraft.trim().slice(0, LIMITS.noteChars), noteAt: Date.now() }, { touch: false });
  };

  const upcoming = solved ? nextPuzzle(id) : null;
  const goNext = () => {
    saveNote();
    setAhaOpen(false);
    if (upcoming) navigate("/p/" + upcoming);
    else navigate("/today");
  };

  const share = async () => {
    const text = `I just solved "${info.title}" on Ponder in ${clock(seconds)}: ten minutes a day, my own brain, no AI.`;
    const url = "https://ponder.sara-aminpour.com";
    try {
      if (navigator.share) await navigator.share({ title: "Ponder", text, url });
      else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast("Copied. Send it to someone who likes puzzles!");
      }
    } catch {
      /* cancelled */
    }
  };

  const muscleClass = meta.muscle.toLowerCase();
  const answerAvailable = hintLevel >= 3 && wait === 0 && !solved;

  return (
    <Shell immersive>
      <div className="wrap puzzle-page">
        <div className="puzzle-top">
          <Link to="/today" className="back-link"><ArrowLeft size={18} /> Today</Link>
          <span className={"chip " + muscleClass}>{meta.muscle}</span>
          <span className="chip">{info.level}</span>
        </div>

        <div className="puzzle-grid">
          <section className="puzzle-main" aria-label="Puzzle">
            <h1 className="puzzle-title">{info.title}</h1>
            <p className="statement">{info.statement.replace(/\.$/, "")}.</p>
            <Board
              id={id}
              saved={saved}
              hintLevel={hintLevel}
              showAnswer={answerOn && !solved}
              restartToken={restartToken}
              onState={onState}
              onSolved={onSolved}
              onPeek={() => undefined}
            />
          </section>

          <aside className="puzzle-side">
            <div className="card side-card timer-card">
              <div className="side-label"><Timer size={16} /> Thinking time</div>
              <div className={"timer-n" + (paused ? " paused" : "")} aria-live="off">{clock(seconds)}</div>
              <p className="muted small">
                {solved ? (exploring ? "Time spent exploring still counts toward today. Your solve time stays as it was." : "Solved. The clock has stopped.") : paused ? "Paused: waiting for you. Touch anything to carry on." : "Only real time counts. Quiet thinking is welcome."}
              </p>
            </div>

            {!solved && (
              <div className="card side-card hint-card">
                <div className="side-label"><HelpCircle size={16} /> Pebble's nudges</div>
                {hintLevel === 0 && <p className="muted small">Stuck is where the thinking happens. Give it a real try first; nudges are questions, never answers.</p>}
                <ol className="nudges">
                  {hints.slice(0, hintLevel).map((h, i) => (
                    <li key={i}><span className="nudge-n">{i + 1}</span><span>{h}</span></li>
                  ))}
                </ol>
                {hintLevel < 3 ? (
                  <Button size="small" onClick={takeNudge} disabled={wait > 0}>
                    <Leaf size={16} /> {wait > 0 ? `Nudge ${hintLevel + 1} in ${clock(wait)}` : `Give me nudge ${hintLevel + 1}`}
                  </Button>
                ) : answerOn ? (
                  <Button size="small" onClick={() => setAnswerOn(false)}><EyeOff size={16} /> Hide the answer</Button>
                ) : revealed ? (
                  <Button size="small" onClick={() => setAnswerOn(true)}><Eye size={16} /> Show the answer again</Button>
                ) : (
                  <Button size="small" onClick={() => setConfirmReveal(true)} disabled={!answerAvailable}>
                    <Eye size={16} /> {answerAvailable ? "Show me the answer" : `Answer in ${clock(wait)}`}
                  </Button>
                )}
              </div>
            )}

            {solved && (
              <div className="card side-card aha-card">
                <div className="side-label"><Sparkles size={16} /> Solved: {rec?.result}</div>
                <p className="small"><strong>The idea.</strong> {meta.idea}</p>
                <p className="small muted"><strong>Think further.</strong> {further}</p>
                <label className="field">
                  <span>In your own words</span>
                  <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onBlur={saveNote} maxLength={LIMITS.noteChars} placeholder="What did you try first? What was the trick?" />
                </label>
                <div className="row-gap">
                  <Button kind="primary" onClick={goNext}>{upcoming ? "Next puzzle" : "Back to today"} <ArrowRight size={17} /></Button>
                  <Button size="small" onClick={() => setConfirmRestart(true)}><RotateCcw size={16} /> Play again</Button>
                </div>
              </div>
            )}

            {!solved && (
              <Button kind="ghost" size="small" onClick={() => (rec?.state ? setConfirmRestart(true) : restart())} className="restart-btn">
                <RotateCcw size={16} /> Start this puzzle over
              </Button>
            )}
          </aside>
        </div>
      </div>

      <Modal open={ahaOpen} onClose={() => { saveNote(); setAhaOpen(false); }} title="Solved!" wide>
        <div className="aha">
          <img src="/art/celebrate.webp" alt="" width="180" height="180" className="aha-art" />
          <div className="aha-body">
            <p className="kicker">Aha!</p>
            <h2>You solved it: {rec?.result}</h2>
            <p className="muted">
              {clock(seconds)} of thinking{rec && rec.hints > 0 ? ` · ${rec.hints} nudge${rec.hints === 1 ? "" : "s"}` : " · no nudges"}
            </p>
            <p><strong>The idea.</strong> {meta.idea}</p>
            <p className="muted"><strong>Think further.</strong> {further}</p>
            <label className="field">
              <span>In your own words <span className="muted">(only you see this; explaining it is what makes it stick)</span></span>
              <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} maxLength={LIMITS.noteChars} placeholder="What did you try first? What was the trick?" />
            </label>
          </div>
        </div>
        <div className="modal-foot">
          <Button kind="primary" size="big" onClick={goNext}>{upcoming ? "Next puzzle" : "Back to today"} <ArrowRight size={18} /></Button>
          <Button onClick={() => { saveNote(); setAhaOpen(false); }}>Stay here</Button>
          <Button kind="ghost" onClick={share}><Share2 size={16} /> Share</Button>
        </div>
      </Modal>

      <Modal open={confirmReveal} onClose={() => setConfirmReveal(false)} title="Show the answer?">
        <h2>Show the answer?</h2>
        <p className="muted" style={{ margin: "10px 0 18px" }}>
          It won't count as solved, but you can hide it and keep trying; if you solve it yourself afterwards it counts as solved "with a peek".
        </p>
        <div className="row-gap">
          <Button kind="pond" onClick={revealAnswer}>Show me</Button>
          <Button onClick={() => setConfirmReveal(false)}>Keep thinking</Button>
        </div>
      </Modal>

      <Modal open={confirmRestart} onClose={() => setConfirmRestart(false)} title="Start over?">
        <h2>Start over?</h2>
        <p className="muted" style={{ margin: "10px 0 18px" }}>The board goes back to empty. Your thinking time and nudges are kept.</p>
        <div className="row-gap">
          <Button kind="pond" onClick={restart}>Start over</Button>
          <Button onClick={() => setConfirmRestart(false)}>Cancel</Button>
        </div>
      </Modal>
    </Shell>
  );
}

